import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { StatCard } from '../components/StatCard';

import { useAdminDashboard } from '../hooks/useAdminDashboard';
import { COLORS } from '../../../../core/constants/colors';
import { recommendationsService } from '../../../recommendations/services/recommendations.service';

export const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { stats, recentActivity, completeData, isLoading } = useAdminDashboard();
  const [, setRecommendationsHealth] = useState<string>('checking...');
  const [, setRecommendationsHealthy] = useState<boolean>(true);

  useEffect(() => {
    checkRecommendationsHealth();
  }, []);

  const checkRecommendationsHealth = async () => {
    try {
      const status = await recommendationsService.checkHealth();
      setRecommendationsHealth(status || 'healthy');
      setRecommendationsHealthy(true);
    } catch (err) {
      setRecommendationsHealth('offline');
      setRecommendationsHealthy(false);
    }
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return { bg: '#E8F5E9', text: '#2E7D32', border: '#4CAF50' };
      case 'degraded':
        return { bg: '#FFF3E0', text: '#E65100', border: '#FF9800' };
      case 'unhealthy':
        return { bg: '#FFEBEE', text: '#C62828', border: '#F44336' };
      default:
        return { bg: '#F5F5F5', text: '#757575', border: '#9E9E9E' };
    }
  };

  if (isLoading) {
    return (
        <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: '#6B7280' }}>Loading dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      pageTitle="Dashboard"
      pageDescription="Monitor portfolio companies, documents, and AI query activity across your indexing system"
    >
      <div style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-[1400px] mx-auto px-8 py-8">
        {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <StatCard
            title="Total Companies"
            value={stats.totalCompanies}
            subtitle="Portfolio companies"
            icon={
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
              </svg>
            }
              iconBgColor="#EEF2FF"
              iconColor="#6366F1"
            onClick={() => navigate('/admin/companies')}
          />

          <StatCard
            title="Total Documents"
            value={stats.totalDocuments}
            subtitle="Indexed files"
            icon={
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
              </svg>
            }
            iconBgColor="#E8F4FF"
              iconColor="#0E81FF"
              onClick={() => navigate('/admin/documents')}
          />

          <StatCard
            title="Total Queries"
            value={stats.totalQueries}
            subtitle="AI queries executed"
            icon={
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" />
              </svg>
            }
              iconBgColor="#FCDAC8"
              iconColor="#FF5E2E"
              onClick={() => navigate('/admin/queries')}
          />

          <StatCard
            title="Active Companies"
            value={stats.activeCompanies}
            subtitle="Currently active"
            icon={
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            }
              iconBgColor="#DEFADE"
              iconColor="#8CE58C"
          />

          <StatCard
            title="Processing Documents"
            value={stats.processingDocuments}
            subtitle="Currently processing"
            icon={
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
            }
            iconBgColor="#FFF3E0"
            iconColor="#E65100"
            onClick={() => navigate('/admin/documents?status=processing')}
          />

          <StatCard
            title="Pending ETL Tasks"
            value={stats.pendingETLTasks}
            subtitle="Tasks in queue"
            icon={
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
              </svg>
            }
            iconBgColor="#E3F2FD"
            iconColor="#1565C0"
            onClick={() => navigate('/admin/etl-tasks?status=pending')}
          />

          <StatCard
            title="System Health"
            value={completeData?.system_health?.overall_status === 'healthy' ? 'Healthy' : completeData?.system_health?.overall_status === 'degraded' ? 'Degraded' : 'Unhealthy'}
            subtitle={`${completeData?.system_health?.recommendations?.length || 0} recommendations`}
            icon={
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            }
            iconBgColor={completeData?.system_health?.overall_status === 'healthy' ? '#E8F5E9' : completeData?.system_health?.overall_status === 'degraded' ? '#FFF3E0' : '#FFEBEE'}
            iconColor={completeData?.system_health?.overall_status === 'healthy' ? '#2E7D32' : completeData?.system_health?.overall_status === 'degraded' ? '#E65100' : '#C62828'}
            onClick={() => navigate('/admin/system-health')}
          />
        </div>

          {/* System Health, ETL Status & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Left Column: System Health & ETL Status */}
          <div className="space-y-6">
            {/* System Health */}
            {completeData?.system_health && (
            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold" style={{ color: '#1F2937' }}>
                    System Health
            </h3>
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-semibold"
                    style={{
                      backgroundColor: getHealthStatusColor(completeData.system_health.overall_status).bg,
                      color: getHealthStatusColor(completeData.system_health.overall_status).text,
                    }}
                  >
                    {completeData.system_health.overall_status.toUpperCase()}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {Object.entries(completeData.system_health.services).map(([service, status]) => {
                    if (!status) return null;
                    return (
                      <div key={service} className="flex items-center justify-between p-2 rounded" style={{ backgroundColor: '#F9FAFB' }}>
                        <span className="text-sm font-medium capitalize" style={{ color: '#6B7280' }}>
                          {service.replace(/_/g, ' ')}
                        </span>
                        <span 
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: getHealthStatusColor(status.status).bg,
                            color: getHealthStatusColor(status.status).text,
                          }}
                        >
                          {status.status}
                        </span>
                    </div>
                    );
                  })}
                </div>
                {completeData.system_health.recommendations && completeData.system_health.recommendations.length > 0 && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: '#E5E7EB' }}>
                    <p className="text-xs font-semibold mb-2" style={{ color: '#6B7280' }}>Recommendations:</p>
                    <ul className="space-y-1">
                      {completeData.system_health.recommendations.map((rec, idx) => (
                        <li key={idx} className="text-xs" style={{ color: '#9CA3AF' }}>
                          • {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* ETL Status */}
            {completeData?.etl_status && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#1F2937' }}>
                  ETL Status
                </h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Pending Tasks</p>
                    <p className="text-2xl font-bold" style={{ color: '#1F2937' }}>
                      {completeData.etl_status.pending_tasks}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Processing</p>
                    <p className="text-2xl font-bold" style={{ color: '#1F2937' }}>
                      {completeData.etl_status.processing_tasks}
                    </p>
                    </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Completed (24h)</p>
                    <p className="text-2xl font-bold" style={{ color: '#2E7D32' }}>
                      {completeData.etl_status.completed_tasks_24h}
                    </p>
                </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Failed (24h)</p>
                    <p className="text-2xl font-bold" style={{ color: '#C62828' }}>
                      {completeData.etl_status.failed_tasks_24h}
                    </p>
                    </div>
                </div>
                {completeData.etl_status.average_processing_time_seconds > 0 && (
                  <div className="pt-4 border-t" style={{ borderColor: '#E5E7EB' }}>
                    <p className="text-xs text-gray-500 mb-1">Avg Processing Time</p>
                    <p className="text-sm font-semibold" style={{ color: '#1F2937' }}>
                      {completeData.etl_status.average_processing_time_seconds.toFixed(1)}s
                    </p>
                  </div>
                )}
            </div>
            )}
          </div>

            {/* Right Column: Recent Queries */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold" style={{ color: '#1F2937' }}>
                  Recent Queries
            </h3>
                <button 
                  onClick={() => navigate('/admin/queries')}
                  className="text-sm font-medium hover:underline"
                  style={{ color: COLORS.admin.primary }}
                >
                  View all
                </button>
              </div>
            <div className="space-y-3">
                {recentActivity && recentActivity.length > 0 ? (
                  recentActivity
                    .slice(0, 5)
                    .map((activity) => (
                  <div
                    key={activity.id}
                        className="flex items-start gap-3 p-3 rounded-lg transition-all cursor-pointer hover:bg-gray-50"
                        style={{ border: '1px solid #E5E7EB' }}
                        onClick={() => navigate('/admin/queries')}
                  >
                    <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: '#FCDAC8' }}
                    >
                          <svg className="w-4 h-4" style={{ color: '#FF5E2E' }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm mb-0.5" style={{ color: '#1F2937' }}>
                        {activity.metadata?.company_name || 'All Companies'}
                      </p>
                          <p className="text-xs truncate" style={{ color: '#6B7280' }}>
                        {activity.description}
                      </p>
                      {activity.metadata?.response_time_ms && (
                        <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
                          {activity.metadata.response_time_ms}ms
                        </p>
                      )}
                    </div>
                        <span className="text-xs flex-shrink-0" style={{ color: '#9CA3AF' }}>
                      {new Date(activity.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                ))
              ) : (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 mx-auto mb-3" style={{ color: '#D1D5DB' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm mb-2" style={{ color: '#9CA3AF' }}>
                      No recent queries
                    </p>
                <button 
                  onClick={() => navigate('/admin/queries')}
                  className="text-sm font-medium hover:underline"
                  style={{ color: COLORS.admin.primary }}
                >
                  View all
                </button>
                  </div>
              )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

